"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";

type IndexDetailPayload = {
  indexName: string;
  generatedAt: string;
  overview: {
    period: { from: string; to: string };
    totalReturn: number;
    volatility20d: number;
    volatilityPercentile: number | null;
    drawdown: { current: number; max: number };
    regimeLabel: string;
    priceSeries: { date: string; close: number }[];
  };
  breadth: {
    sessionParticipationPct: number;
    avgParticipation20D: number;
    advanceDeclineSeries: { date: string; advancers: number; decliners: number }[];
    newHighs: number | null;
    newLows: number | null;
    upperCircuit?: number | null;
    lowerCircuit?: number | null;
    upperCircuitStocks?: CircuitStock[];
    lowerCircuitStocks?: CircuitStock[];
    relativeStrength: { rsBreadth: { positiveRS60D_pct: number | null } };
  };
  concentration: {
    top5Weight: number;
    top10Weight: number;
    vs3MonthAverage: number;
    top3ImpactPct: number;
    herfindahlScore: number;
  };
  liquidity: {
    vs30DayAvgPct: number;
    volumeTrend: string;
    avgTradedValue20D: number;
    regimeLabel: string;
  };
  interpretation?: {
    overviewText?: string;
    breadthText?: string;
    concentrationText?: string;
    liquidityText?: string;
  };
};

type CircuitRule = {
  type: "pct" | "abs";
  threshold: number;
};

type CircuitStock = {
  symbol: string;
  name?: string | null;
  sector?: string | null;
  close?: number | null;
  change?: number | null;
  change_pct?: number | null;
  volume?: number | null;
  date?: string | null;
  previous_close?: number | null;
  rule?: CircuitRule | null;
};

type LiveIndex = {
  index: string;
  code: string;
  value: number | null;
  change: number | null;
  pct_change: number | null;
};

type LivePayload = {
  source_url: string;
  as_of: string | null;
  indices: LiveIndex[];
};

type WindowSupport = {
  has20D?: boolean;
  has60D?: boolean;
  has90D?: boolean;
  primaryRank?: string;
};

type RSMetricKey = "RS20D" | "RS60D" | "RS90D";

type SectorRSRow = {
  sector: string;
  RS20D?: number | null;
  RS60D?: number | null;
  RS90D?: number | null;
  weightPct?: number | null;
};

type RSLeaderRow = {
  ticker: string;
  RS20D?: number | null;
  RS60D?: number | null;
  RS90D?: number | null;
  sector?: string | null;
};

type RSSummary = {
  windowSupport?: WindowSupport | null;
  leaders?: RSLeaderRow[];
  laggards?: RSLeaderRow[];
  sectorLeaders?: {
    bestSector?: { name: string; avgRS60D?: number | null } | null;
    worstSector?: { name: string; avgRS60D?: number | null } | null;
  } | null;
};

type IndexWeightImpactRow = {
  symbol: string;
  company?: string | null;
  weightPct: number;
  price: number | null;
  impactPerPkr: number | null;
};

type IndexWeightImpactPayload = {
  index: string;
  indexLevel: number | null;
  indexLevelAsOf?: string | null;
  weightsAsOf?: string | null;
  rows: IndexWeightImpactRow[];
};

type WeightedContributorRow = {
  symbol: string;
  company?: string | null;
  weight_pct?: number | null;
  return_pct?: number | null;
  contribution_pct?: number | null;
};

type SectorRSPayload = {
  sectorRS?: SectorRSRow[];
  windowSupport?: WindowSupport | null;
};

const fmt = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });

const fmtMaybe = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return fmt(value, digits);
};

const fmtPctValue = (value: number | null | undefined, digits = 1) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${fmt(value, digits)}%`;
};

const fmtPctDecimal = (value: number | null | undefined, digits = 1) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${fmt(value * 100, digits)}%`;
};

const fmtImpactPoints = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${fmt(value, digits)} pts`;
};

const fmtSignedPct = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${value > 0 ? "+" : ""}${fmt(value, digits)}%`;
};

const fmtPkrCompact = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `Rs ${fmt(value / 1_000_000_000, 1)}B`;
  if (abs >= 1_000_000) return `Rs ${fmt(value / 1_000_000, 1)}M`;
  return `Rs ${fmt(value, 0)}`;
};

const fmtCompactValue = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${fmt(value / 1_000_000_000, 1)}B`;
  if (abs >= 1_000_000) return `${fmt(value / 1_000_000, 1)}M`;
  return fmt(value, 2);
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && !Number.isNaN(value);

const valueColor = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value) || value === 0) return "var(--color-text-muted)";
  return value > 0 ? "var(--color-success)" : "var(--color-danger)";
};

const buildPath = (values: number[], width: number, height: number, min: number, max: number) => {
  if (!values.length) return "";
  const span = max - min || 1;
  return values
    .map((v, i) => {
      const x = (i / Math.max(1, values.length - 1)) * width;
      const y = height - ((v - min) / span) * height;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
};

const normalizeCode = (value: string) => value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

const RS_WINDOW_LABELS: Record<RSMetricKey, string> = {
  RS20D: "15 sessions",
  RS60D: "30 sessions",
  RS90D: "45 sessions",
};

const getPrimaryRsKey = (support?: WindowSupport | null): RSMetricKey => {
  if (support?.primaryRank === "RS60D" || support?.primaryRank === "RS90D") {
    return support.primaryRank;
  }
  return "RS20D";
};

const getRsValue = (
  row: { RS20D?: number | null; RS60D?: number | null; RS90D?: number | null } | null | undefined,
  key: RSMetricKey,
) => {
  if (!row) return null;
  const value = row[key];
  return isFiniteNumber(value) ? value : null;
};

const parseMaybeNumber = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const normalizeLivePayload = (raw: unknown): LivePayload | null => {
  if (!raw || typeof raw !== "object") return null;
  const payload = raw as Record<string, unknown>;
  const rawIndices = Array.isArray(payload.indices) ? payload.indices : [];
  const indices: LiveIndex[] = rawIndices
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const index = String(row.index ?? row.name ?? row.code ?? "").trim();
      const code = String(row.code ?? row.index ?? row.name ?? "").trim();
      if (!index && !code) return null;
      return {
        index,
        code,
        value: parseMaybeNumber(row.value ?? row.index_value ?? row.current_value ?? row.level),
        change: parseMaybeNumber(row.change ?? row.point_change ?? row.net_change),
        pct_change: parseMaybeNumber(row.pct_change ?? row.change_pct ?? row.percent_change ?? row.percent),
      };
    })
    .filter((row): row is LiveIndex => row !== null);

  return {
    source_url: typeof payload.source_url === "string" ? payload.source_url : "",
    as_of:
      typeof payload.as_of === "string"
        ? payload.as_of
        : typeof payload.snapshot_at === "string"
          ? payload.snapshot_at
          : typeof payload.snapshot_date === "string"
            ? payload.snapshot_date
            : null,
    indices,
  };
};

const INDEX_CODE_MAP: Record<string, string> = {
  "KSE-100": "KSE100",
  "KSE-ALL-SHARES": "ALLSHR",
  "KMI-30": "KMI30",
  "MII30": "MII30",
  "PSXDIV20": "PSXDIV20",
};

const INDEX_OPTIONS = [
  {
    value: "KSE-100",
    label: "KSE-100",
    description: "Benchmark",
  },
  {
    value: "KSE-ALL-SHARES",
    label: "KSE All Shares",
    description: "Full market",
  },
  {
    value: "KMI-30",
    label: "KMI-30",
    description: "Islamic 30",
  },
  {
    value: "MII30",
    label: "MII30",
    description: "Investable Islamic",
  },
  {
    value: "PSXDIV20",
    label: "PSXDIV20",
    description: "Dividend 20",
  },
] as const;

export default function IndexDetailPage() {
  const params = useParams<{ index: string }>();
  const indexParam = typeof params?.index === "string" ? params.index : "KSE-100";
  const indexOptions = INDEX_OPTIONS.map((option) => option.value);
  const indexName = indexOptions.includes(indexParam as (typeof indexOptions)[number]) ? indexParam : "KSE-100";
  const activeIndexOption = INDEX_OPTIONS.find((option) => option.value === indexName) ?? INDEX_OPTIONS[0];

  const [detailPayload, setDetailPayload] = useState<IndexDetailPayload | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [live, setLive] = useState<LivePayload | null>(null);
  const [sectorRS, setSectorRS] = useState<SectorRSRow[]>([]);
  const [sectorWindowSupport, setSectorWindowSupport] = useState<WindowSupport | null>(null);
  const [rsSummary, setRsSummary] = useState<RSSummary | null>(null);
  const [impactPayload, setImpactPayload] = useState<IndexWeightImpactPayload | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState<string | null>(null);
  const [impactFallbackRows, setImpactFallbackRows] = useState<WeightedContributorRow[]>([]);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        setDetailError(null);
        const url = `/api/indices/detail?index=${encodeURIComponent(indexName)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Index detail fetch failed: ${res.status}`);
        const data = (await res.json()) as IndexDetailPayload;
        setDetailPayload(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load index detail";
        setDetailError(msg);
        setDetailPayload(null);
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetail();
  }, [indexName]);

  useEffect(() => {
    const loadLive = async () => {
      try {
        const res = await fetch("/api/outputs/indices_eod.json");
        if (!res.ok) throw new Error(`EOD fetch failed: ${res.status}`);
        const data = normalizeLivePayload(await res.json());
        setLive(data);
      } catch {
        setLive(null);
      }
    };
    loadLive();
  }, []);

  useEffect(() => {
    const loadOutperformers = async () => {
      try {
        const [sectorRes, summaryRes] = await Promise.all([
          fetch(`/api/indices/relative-strength/sectors?index=${encodeURIComponent(indexName)}`),
          fetch(`/api/indices/relative-strength/summary?index=${encodeURIComponent(indexName)}`),
        ]);

        if (sectorRes.ok) {
          const data = (await sectorRes.json()) as SectorRSPayload;
          const rows = Array.isArray(data?.sectorRS) ? data.sectorRS : [];
          setSectorRS(rows);
          setSectorWindowSupport(data?.windowSupport || null);
        } else {
          setSectorRS([]);
          setSectorWindowSupport(null);
        }

        if (summaryRes.ok) {
          const data = (await summaryRes.json()) as RSSummary;
          setRsSummary(data);
        } else {
          setRsSummary(null);
        }
      } catch {
        setSectorRS([]);
        setSectorWindowSupport(null);
        setRsSummary(null);
      }
    };
    loadOutperformers();
  }, [indexName]);

  useEffect(() => {
    const loadImpact = async () => {
      try {
        setImpactLoading(true);
        setImpactError(null);
        setImpactFallbackRows([]);
        const res = await fetch(
          `/api/market-intelligence/index-weight-impact?index=${encodeURIComponent(indexName)}&limit=15`
        );
        if (!res.ok) throw new Error(`Impact fetch failed: ${res.status}`);
        const data = (await res.json()) as IndexWeightImpactPayload;
        setImpactPayload(data);
      } catch (err) {
        setImpactPayload(null);
        try {
          const fallbackRes = await fetch(`/api/market-intelligence?index=${encodeURIComponent(indexName)}`);
          if (!fallbackRes.ok) throw new Error(`Fallback fetch failed: ${fallbackRes.status}`);
          const fallbackData = (await fallbackRes.json()) as {
            inputs?: { top_weighted_contributors?: WeightedContributorRow[] };
          };
          const rows = Array.isArray(fallbackData?.inputs?.top_weighted_contributors)
            ? fallbackData.inputs.top_weighted_contributors.slice(0, 10)
            : [];
          setImpactFallbackRows(rows);
          setImpactError(rows.length ? null : err instanceof Error ? err.message : "Failed to load impact data");
        } catch (fallbackErr) {
          setImpactFallbackRows([]);
          setImpactError(fallbackErr instanceof Error ? fallbackErr.message : "Failed to load impact data");
        }
      } finally {
        setImpactLoading(false);
      }
    };
    loadImpact();
  }, [indexName]);

  const sectorPrimaryKey = useMemo(
    () => getPrimaryRsKey(sectorWindowSupport ?? rsSummary?.windowSupport),
    [sectorWindowSupport, rsSummary],
  );
  const summaryPrimaryKey = useMemo(() => getPrimaryRsKey(rsSummary?.windowSupport), [rsSummary]);
  const sectorPrimaryLabel = RS_WINDOW_LABELS[sectorPrimaryKey];
  const summaryPrimaryLabel = RS_WINDOW_LABELS[summaryPrimaryKey];

  const liveIndex = useMemo(() => {
    if (!live?.indices?.length) return null;
    const code = INDEX_CODE_MAP[indexName] || normalizeCode(indexName);
    return (
      live.indices.find(
        (i) =>
          normalizeCode(i.code) === code ||
          normalizeCode(i.index) === code ||
          i.index.toLowerCase() === indexName.toLowerCase()
      ) || null
    );
  }, [live, indexName]);

  const topSectors = useMemo(() => {
    return [...sectorRS]
      .filter((row) => getRsValue(row, sectorPrimaryKey) !== null)
      .sort((a, b) => (getRsValue(b, sectorPrimaryKey) ?? 0) - (getRsValue(a, sectorPrimaryKey) ?? 0))
      .slice(0, 6);
  }, [sectorRS, sectorPrimaryKey]);

  const topStocks = useMemo(() => {
    const leaders = rsSummary?.leaders || [];
    return [...leaders]
      .filter((row) => getRsValue(row, summaryPrimaryKey) !== null)
      .sort((a, b) => (getRsValue(b, summaryPrimaryKey) ?? 0) - (getRsValue(a, summaryPrimaryKey) ?? 0))
      .slice(0, 6);
  }, [rsSummary, summaryPrimaryKey]);

  const laggardStocks = useMemo(() => {
    const laggards = rsSummary?.laggards || [];
    return [...laggards]
      .filter((row) => getRsValue(row, summaryPrimaryKey) !== null)
      .sort((a, b) => (getRsValue(a, summaryPrimaryKey) ?? 0) - (getRsValue(b, summaryPrimaryKey) ?? 0))
      .slice(0, 6);
  }, [rsSummary, summaryPrimaryKey]);

  const sectorLeadership = useMemo(() => {
    const bestFallback = topSectors[0]
      ? { name: topSectors[0].sector, primaryValue: getRsValue(topSectors[0], sectorPrimaryKey) }
      : null;
    const worstSectorRow = [...sectorRS]
      .filter((row) => getRsValue(row, sectorPrimaryKey) !== null)
      .sort((a, b) => (getRsValue(a, sectorPrimaryKey) ?? 0) - (getRsValue(b, sectorPrimaryKey) ?? 0))[0];
    const worstFallback = worstSectorRow
      ? { name: worstSectorRow.sector, primaryValue: getRsValue(worstSectorRow, sectorPrimaryKey) }
      : null;

    return {
      best: bestFallback ?? (
        rsSummary?.sectorLeaders?.bestSector
          ? {
              name: rsSummary.sectorLeaders.bestSector.name,
              primaryValue: rsSummary.sectorLeaders.bestSector.avgRS60D ?? null,
            }
          : null
      ),
      worst: worstFallback ?? (
        rsSummary?.sectorLeaders?.worstSector
          ? {
              name: rsSummary.sectorLeaders.worstSector.name,
              primaryValue: rsSummary.sectorLeaders.worstSector.avgRS60D ?? null,
            }
          : null
      ),
    };
  }, [rsSummary, sectorRS, topSectors, sectorPrimaryKey]);

  const impactSummary = useMemo(() => {
    const rows = impactPayload?.rows || [];
    const validRows = rows.filter((row) => typeof row.impactPerPkr === "number");
    if (!validRows.length) return null;

    const topFive = validRows.slice(0, 5);
    return {
      leader: validRows[0],
      topFiveImpact: topFive.reduce((sum, row) => sum + (row.impactPerPkr ?? 0), 0),
      topFiveWeight: topFive.reduce((sum, row) => sum + row.weightPct, 0),
      coveredRows: validRows.length,
    };
  }, [impactPayload]);

  const breadthSummary = useMemo(() => {
    if (!detailPayload) return null;
    const participation = detailPayload.breadth.sessionParticipationPct;
    const avgParticipation = detailPayload.breadth.avgParticipation20D;
    const rsBreadth = detailPayload.breadth.relativeStrength.rsBreadth.positiveRS60D_pct;

    const participationTone =
      participation >= 60 ? "strong" : participation >= 45 ? "neutral" : "weak";
    const rsTone =
      rsBreadth == null ? null : rsBreadth >= 60 ? "broad" : rsBreadth >= 40 ? "moderate" : "selective";

    const participationLine = `Participation is ${fmtPctValue(participation)} today vs ${fmtPctValue(avgParticipation)} (${participationTone}).`;
    const rsLine =
      rsBreadth == null
        ? "RS breadth is unavailable for this session."
        : `About ${fmtPctValue(rsBreadth)} of stocks are outperforming over 30 sessions, so leadership is ${rsTone}.`;

    return `${participationLine} ${rsLine}`;
  }, [detailPayload]);

  const breadthRead = useMemo(() => {
    const series = detailPayload?.breadth.advanceDeclineSeries || [];
    const latest = series[series.length - 1];
    if (!latest) return null;

    const netAdvancers = latest.advancers - latest.decliners;
    const total = latest.advancers + latest.decliners;
    const advancerShare = total > 0 ? (latest.advancers / total) * 100 : null;
    const label =
      netAdvancers >= 15
        ? "Broad buying"
        : netAdvancers <= -15
          ? "Broad selling"
          : "Mixed tape";
    const detail =
      netAdvancers >= 15
        ? "More index members are rising than falling, so the move has healthier participation."
        : netAdvancers <= -15
          ? "More index members are falling than rising, so be stricter with new long entries."
          : "The up and down lists are close, so index direction may be driven by fewer heavy stocks.";

    return {
      date: latest.date,
      advancers: latest.advancers,
      decliners: latest.decliners,
      netAdvancers,
      advancerShare,
      label,
      detail,
    };
  }, [detailPayload]);

  const concentrationRead = useMemo(() => {
    if (!detailPayload) return null;
    const top10Weight = detailPayload.concentration.top10Weight;
    const top3Impact = detailPayload.concentration.top3ImpactPct;
    const crowdingDelta = detailPayload.concentration.vs3MonthAverage;

    const label =
      top10Weight >= 60
        ? "Heavyweight-driven"
        : top10Weight >= 45
          ? "Moderately concentrated"
          : "Broadly distributed";
    const detail =
      top10Weight >= 60
        ? "The index can move even if many smaller members are quiet. Confirm breadth before chasing breakouts."
        : top10Weight >= 45
          ? "Large stocks still matter, but breadth and sector leadership can give useful confirmation."
          : "The index move is less dependent on a small group of heavyweight names.";

    return {
      label,
      detail,
      top10Weight,
      top3Impact,
      crowdingDelta,
    };
  }, [detailPayload]);

  const liquidityRead = useMemo(() => {
    if (!detailPayload) return null;
    const vsAverage = detailPayload.liquidity.vs30DayAvgPct;
    const label = vsAverage >= 20 ? "High activity" : vsAverage <= -20 ? "Dry volume" : "Normal activity";
    const detail =
      vsAverage >= 20
        ? "Breakouts and breakdowns have better follow-through when traded value is above normal."
        : vsAverage <= -20
          ? "Moves can fade faster when money is not active. Use smaller size or wait for confirmation."
          : "Activity is close to normal. Prefer setups with clean breadth and sector support.";

    return {
      label,
      detail,
      vsAverage,
    };
  }, [detailPayload]);

  const trendRead = useMemo(() => {
    const series = detailPayload?.overview.priceSeries || [];
    const closes = series.map((point) => point.close).filter((value) => isFiniteNumber(value));
    if (!closes.length) return null;
    const last = closes[closes.length - 1];
    const high = Math.max(...closes);
    const low = Math.min(...closes);
    return { last, high, low };
  }, [detailPayload]);

  const retailRead = useMemo(() => {
    if (!detailPayload) return null;
    const totalReturn = detailPayload.overview.totalReturn;
    const breadthNet = breadthRead?.netAdvancers ?? 0;
    const liquidity = detailPayload.liquidity.vs30DayAvgPct;
    const top10Weight = detailPayload.concentration.top10Weight;

    if (totalReturn < -3 || breadthNet <= -15) {
      return {
        label: "Defensive market",
        action: "Avoid chasing. Wait for breadth to improve before taking fresh longs.",
        evidence: [
          `3M return ${fmtPctValue(totalReturn)}`,
          breadthRead ? `${breadthRead.advancers} up / ${breadthRead.decliners} down` : "Breadth unavailable",
          liquidityRead?.label || "Activity unavailable",
        ],
        tone: "caution" as const,
      };
    }

    if (top10Weight >= 60 && breadthNet <= 0) {
      return {
        label: "Heavyweight-led move",
        action: "Be selective. Index strength may not be flowing into the wider list.",
        evidence: [
          `Top 10 control ${fmtPctValue(top10Weight, 1)}`,
          breadthRead ? `${breadthRead.advancers} up / ${breadthRead.decliners} down` : "Breadth unavailable",
          liquidityRead?.label || "Activity unavailable",
        ],
        tone: "neutral" as const,
      };
    }

    if (totalReturn > 0 && breadthNet > 0 && liquidity >= -10) {
      return {
        label: "Constructive for selective longs",
        action: "Long setups have better odds when breadth, trend, and activity are aligned.",
        evidence: [
          `3M return ${fmtPctValue(totalReturn)}`,
          breadthRead ? `${breadthRead.advancers} up / ${breadthRead.decliners} down` : "Breadth unavailable",
          liquidityRead?.label || "Activity unavailable",
        ],
        tone: "positive" as const,
      };
    }

    return {
      label: "Selective market",
      action: "Trade only the cleanest setups. Confirm strength with breadth and sector leadership.",
      evidence: [
        `3M return ${fmtPctValue(totalReturn)}`,
        breadthRead?.label || "Breadth unavailable",
        concentrationRead?.label || "Concentration unavailable",
      ],
      tone: "neutral" as const,
    };
  }, [breadthRead, concentrationRead, detailPayload, liquidityRead]);

  const faqItems = useMemo(
    () => [
      {
        question: `How should a retail trader use the ${activeIndexOption.label} dashboard?`,
        answer:
          "Start with the Retail Read, then confirm whether more stocks are rising than falling, whether strong stocks are beating the index, and whether traded value is active enough for follow-through.",
      },
      {
        question: "What does breadth mean on this page?",
        answer:
          "Breadth shows whether the index move is supported by many stocks or only a few large names. More advancers than decliners usually means healthier participation.",
      },
      {
        question: "What does heavyweight-driven mean?",
        answer:
          "Heavyweight-driven means a small group of large stocks controls a big part of the index move. In that case, the index can rise while many smaller stocks stay weak.",
      },
    ],
    [activeIndexOption.label],
  );

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    }),
    [faqItems],
  );

  const snapshotCards = useMemo(() => {
    if (!detailPayload) return [];

    return [
      {
        label: "Live Level",
        value: liveIndex?.value != null ? fmtMaybe(liveIndex.value, 2) : "—",
        meta:
          liveIndex?.pct_change != null
            ? `${fmtMaybe(liveIndex.change, 2)} / ${fmtMaybe(liveIndex.pct_change, 2)}%`
            : live?.as_of
              ? `As of ${live.as_of}`
              : "Waiting for live snapshot",
      },
      {
        label: "3M Return",
        value: fmtPctValue(detailPayload.overview.totalReturn),
        meta: detailPayload.overview.regimeLabel || "Trend regime",
        accent: valueColor(detailPayload.overview.totalReturn),
      },
      {
        label: "Participation",
        value: fmtPctValue(detailPayload.breadth.sessionParticipationPct),
        meta: `20D avg ${fmtPctValue(detailPayload.breadth.avgParticipation20D)}`,
        accent: valueColor(detailPayload.breadth.sessionParticipationPct - detailPayload.breadth.avgParticipation20D),
      },
      {
        label: "Top 10 Control",
        value: fmtPctValue(detailPayload.concentration.top10Weight),
        meta: `Top 5 ${fmtPctValue(detailPayload.concentration.top5Weight)}`,
      },
      {
        label: "Best Sector",
        value: sectorLeadership.best?.name || "—",
        meta:
          sectorLeadership.best?.primaryValue != null
            ? `${sectorPrimaryLabel} ${fmtPctDecimal(sectorLeadership.best.primaryValue, 1)}`
            : "Sector leader unavailable",
        accent: valueColor(sectorLeadership.best?.primaryValue),
      },
      {
        label: "Leading Stock",
        value: topStocks[0]?.ticker || "—",
        meta:
          getRsValue(topStocks[0], summaryPrimaryKey) != null
            ? `${topStocks[0]?.sector || "Sector N/A"} · ${summaryPrimaryLabel} ${fmtPctDecimal(getRsValue(topStocks[0], summaryPrimaryKey), 1)}`
            : "Stock leader unavailable",
        accent: valueColor(getRsValue(topStocks[0], summaryPrimaryKey)),
      },
    ];
  }, [
    detailPayload,
    liveIndex,
    live,
    sectorLeadership,
    topStocks,
    sectorPrimaryLabel,
    summaryPrimaryKey,
    summaryPrimaryLabel,
  ]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container index-dashboard-hero">
          <div className="index-dashboard-header-row">
            <div className="index-dashboard-title-block">
              <div className="index-dashboard-kicker">PSX index dashboard</div>
              <h1 className="page-title">{activeIndexOption.label}</h1>
              <p className="page-subtitle">
                Live level, breadth, concentration, liquidity, and leadership context for the last 3 months.
              </p>
            </div>
            <div className="index-dashboard-actions">
              <Link href={`/indices/${encodeURIComponent(indexName)}/relative-strength`} className="ui-button ui-button-ghost">
                Open Relative Strength
              </Link>
            </div>
          </div>

          <nav className="index-switcher" aria-label="Switch index dashboard">
            {INDEX_OPTIONS.map((option) => {
              const isActive = option.value === indexName;
              return (
                <Link
                  key={option.value}
                  href={`/indices/${encodeURIComponent(option.value)}`}
                  className={`index-switcher-link${isActive ? " index-switcher-link--active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span>{option.label}</span>
                  <em>{option.description}</em>
                </Link>
              );
            })}
          </nav>

          {(liveIndex || live?.as_of) && (
            <div className="index-live-strip">
              <div>
                <span>Live snapshot</span>
                <strong>{liveIndex?.value != null ? fmtMaybe(liveIndex.value, 2) : "Awaiting data"}</strong>
              </div>
              {liveIndex?.pct_change != null && (
                <div>
                  <span>Session move</span>
                  <strong style={{ color: valueColor(liveIndex.pct_change) }}>
                    {fmtMaybe(liveIndex.change, 2)} / {fmtMaybe(liveIndex.pct_change, 2)}%
                  </strong>
                </div>
              )}
              {live?.as_of && (
                <div>
                  <span>As of</span>
                  <strong>{live.as_of}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {detailLoading && (
            <div style={{ color: "var(--color-text-muted)" }}>Loading index detail...</div>
          )}
          {detailError && (
            <div style={{ color: "var(--color-danger)" }}>{detailError}</div>
          )}

          {!detailLoading && !detailError && detailPayload && (
            <div style={{ display: "grid", gap: "2rem" }}>
              <nav className="index-section-nav" aria-label="Index dashboard sections">
                <a href="#retail-read">Retail Read</a>
                <a href="#index-pulse">Pulse</a>
                <a href="#breadth">Breadth</a>
                <a href="#strong-stocks">Relative Strength</a>
                <a href="#heavyweights">Heavyweights</a>
                <a href="#liquidity">Trading Activity</a>
                <a href="#trend">Trend</a>
              </nav>

              <div id="retail-read" style={{ order: 1 }}>
                {retailRead && (
                  <div
                    className={`index-retail-read index-retail-read--${retailRead.tone}`}
                  >
                    <div>
                      <div className="index-retail-read-kicker">Retail Read</div>
                      <div className="index-retail-read-title">{retailRead.label}</div>
                      <p>{retailRead.action}</p>
                    </div>
                    <div className="index-retail-read-evidence">
                      {retailRead.evidence.map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div id="index-pulse" style={{ order: 2 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>Index Pulse</div>
                <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
                  {snapshotCards.map((card) => (
                    <div
                      key={card.label}
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>{card.label}</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.25rem", fontWeight: 700, color: card.accent || "inherit" }}>
                        {card.value}
                      </div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        {card.meta}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div id="trend" style={{ order: 8 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>3-Month Trend & Risk</div>
                <div style={{ display: "grid", gap: "1.2rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>3M Return</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.4rem", fontWeight: 700, color: valueColor(detailPayload.overview.totalReturn) }}>
                      {fmtPctValue(detailPayload.overview.totalReturn)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>20D Volatility</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.2rem", fontWeight: 700 }}>
                      {fmtPctValue(detailPayload.overview.volatility20d)}
                    </div>
                    {detailPayload.overview.volatilityPercentile !== null && (
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                        Percentile: {fmtPctValue(detailPayload.overview.volatilityPercentile)}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Drawdown</div>
                    <div style={{ display: "grid", gap: "0.35rem", marginTop: "0.5rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-muted)" }}>Current</span>
                        <span style={{ fontWeight: 700, color: valueColor(detailPayload.overview.drawdown.current) }}>
                          {fmtPctValue(detailPayload.overview.drawdown.current)}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-muted)" }}>Max 3M</span>
                        <span style={{ fontWeight: 700, color: valueColor(detailPayload.overview.drawdown.max) }}>
                          {fmtPctValue(detailPayload.overview.drawdown.max)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Regime</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.1rem", fontWeight: 700 }}>
                      {detailPayload.overview.regimeLabel || "N/A"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "16px",
                    padding: "1rem",
                    marginTop: "1.2rem",
                    background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: "0.6rem" }}>Trend (3M)</div>
                  {detailPayload.overview.priceSeries.length === 0 ? (
                    <div style={{ color: "var(--color-text-muted)" }}>No price series.</div>
                  ) : (
                    (() => {
                      const values = detailPayload.overview.priceSeries.map((p) => p.close);
                      const min = Math.min(...values);
                      const max = Math.max(...values);
                      const path = buildPath(values, 600, 160, min, max);
                      const last = values[values.length - 1] ?? 0;
                      return (
                        <div>
                          <svg viewBox="0 0 600 160" width="100%" height="160" preserveAspectRatio="none" role="img" aria-label={`${activeIndexOption.label} three-month trend`}>
                            <path d={path} fill="none" stroke={valueColor(last)} strokeWidth="2.5" />
                          </svg>
                          {trendRead && (
                            <div className="index-chart-stats">
                              <span title={fmtMaybe(trendRead.last, 2)}>Last close {fmtCompactValue(trendRead.last)}</span>
                              <span title={fmtMaybe(trendRead.high, 2)}>3M high {fmtCompactValue(trendRead.high)}</span>
                              <span title={fmtMaybe(trendRead.low, 2)}>3M low {fmtCompactValue(trendRead.low)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>

                {detailPayload.interpretation?.overviewText && (
                  <div style={{ marginTop: "0.8rem", color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
                    {detailPayload.interpretation.overviewText}
                  </div>
                )}
              </div>

              <div id="weight-impact" style={{ order: 7 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>
                  Heavyweight Sensitivity Details
                </div>
                <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.9rem" }}>
                  Detail view for how large stocks can move {activeIndexOption.label}. Use this after the simpler heavyweight read.
                </div>
                {impactSummary ? (
                  <div
                    style={{
                      display: "grid",
                      gap: "1rem",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Most Sensitive Stock</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>{impactSummary.leader.symbol}</div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        {fmtImpactPoints(impactSummary.leader.impactPerPkr, 2)} per Rs. 1 move
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Top 5 Weight</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>
                        {fmtPctValue(impactSummary.topFiveWeight, 2)}
                      </div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        Combined weight of the five heaviest names
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Top 5 Rs. 1 Impact</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>
                        {fmtImpactPoints(impactSummary.topFiveImpact, 2)}
                      </div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        Aggregate impact if the top five names each move by Rs. 1
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Impact Coverage</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>{impactSummary.coveredRows}</div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        Constituents with usable per-rupee impact values
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gap: "1rem",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Best Sector</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1rem", fontWeight: 700 }}>{sectorLeadership.best?.name || "—"}</div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        {sectorLeadership.best?.primaryValue != null
                          ? `${sectorPrimaryLabel} ${fmtPctDecimal(sectorLeadership.best.primaryValue, 1)}`
                          : "RS leader unavailable"}
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Weakest Sector</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1rem", fontWeight: 700 }}>{sectorLeadership.worst?.name || "—"}</div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        {sectorLeadership.worst?.primaryValue != null
                          ? `${sectorPrimaryLabel} ${fmtPctDecimal(sectorLeadership.worst.primaryValue, 1)}`
                          : "RS laggard unavailable"}
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Leading Stock</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>{topStocks[0]?.ticker || "—"}</div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        {getRsValue(topStocks[0], summaryPrimaryKey) != null
                          ? `${summaryPrimaryLabel} ${fmtPctDecimal(getRsValue(topStocks[0], summaryPrimaryKey), 1)}`
                          : "Leader unavailable"}
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "16px",
                        padding: "1rem",
                        background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Weakest Stock</div>
                      <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>{laggardStocks[0]?.ticker || "—"}</div>
                      <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                        {getRsValue(laggardStocks[0], summaryPrimaryKey) != null
                          ? `${summaryPrimaryLabel} ${fmtPctDecimal(getRsValue(laggardStocks[0], summaryPrimaryKey), 1)}`
                          : "Laggard unavailable"}
                      </div>
                    </div>
                  </div>
                )}
                <div className="market-intel-impact">
                  <div className="market-intel-impact-header">
                    <div>
                      <strong>{impactFallbackRows.length ? "Top Weighted Contributors" : "Top Weighted Stocks"}</strong>
                      <span>
                        {impactFallbackRows.length
                          ? `Weighted contribution snapshot for ${indexName}`
                          : `How much ${indexName} moves if a stock moves by Rs. 1`}
                      </span>
                    </div>
                    {impactPayload?.indexLevel != null && (
                      <div className="market-intel-impact-meta">
                        Index Level {fmtMaybe(impactPayload.indexLevel, 0)}{" "}
                        {impactPayload.indexLevelAsOf ? `· ${impactPayload.indexLevelAsOf}` : ""}
                      </div>
                    )}
                  </div>
                  {impactLoading && <div className="market-intel-impact-note">Loading impact data...</div>}
                  {!impactLoading && !impactError && impactFallbackRows.length > 0 && (
                    <div className="market-intel-impact-note">
                      Per-rupee impact is temporarily unavailable. Showing current weighted contributors instead.
                    </div>
                  )}
                  {impactError && !impactFallbackRows.length && (
                    <div className="market-intel-impact-note">
                      Stock-level impact data is unavailable right now. Showing relative strength leadership instead.
                    </div>
                  )}
                  {!impactLoading && !impactError && impactPayload?.rows?.length ? (
                    <div className="market-intel-impact-table">
                      <div className="market-intel-impact-row market-intel-impact-head">
                        <span>Symbol</span>
                        <span>Weight</span>
                        <span>Price</span>
                        <span>Impact / Rs 1</span>
                      </div>
                      {impactPayload.rows.map((row) => (
                        <div key={row.symbol} className="market-intel-impact-row">
                          <span className="market-intel-impact-symbol">
                            <strong>{row.symbol}</strong>
                            {row.company && <em>{row.company}</em>}
                          </span>
                          <span>{fmtPctValue(row.weightPct, 2)}</span>
                          <span>{row.price != null ? fmtMaybe(row.price, 2) : "—"}</span>
                          <span>{fmtImpactPoints(row.impactPerPkr, 2)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {!impactLoading && !impactError && !impactPayload?.rows?.length && impactFallbackRows.length > 0 ? (
                    <div className="market-intel-impact-table">
                      <div className="market-intel-impact-row market-intel-impact-head">
                        <span>Symbol</span>
                        <span>Weight</span>
                        <span>Return</span>
                        <span>Contribution</span>
                      </div>
                      {impactFallbackRows.map((row) => (
                        <div key={row.symbol} className="market-intel-impact-row">
                          <span className="market-intel-impact-symbol">
                            <strong>{row.symbol}</strong>
                            {row.company && <em>{row.company}</em>}
                          </span>
                          <span>{fmtPctValue(row.weight_pct, 2)}</span>
                          <span style={{ color: valueColor(row.return_pct) }}>{fmtSignedPct(row.return_pct, 2)}</span>
                          <span style={{ color: valueColor(row.contribution_pct) }}>{fmtSignedPct(row.contribution_pct, 2)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {impactPayload?.weightsAsOf && (
                    <div className="market-intel-impact-footnote">
                      Weights as of {impactPayload.weightsAsOf}.
                    </div>
                  )}
                </div>
              </div>

              <div id="strong-stocks" style={{ order: 4 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>
                  Relative Strength (Strong Stocks) vs {activeIndexOption.label}
                </div>
                <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.9rem" }}>
                  These are stocks and sectors beating this index over the best available ranking window. Use them to shortlist names only when breadth and activity agree.
                </div>
                <div
                  style={{
                    display: "grid",
                    gap: "1rem",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Best Sector</div>
                    <div style={{ marginTop: "0.45rem", fontSize: "1rem", fontWeight: 700 }}>{sectorLeadership.best?.name || "—"}</div>
                    <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                      {sectorLeadership.best?.primaryValue != null
                        ? `${sectorPrimaryLabel} ${fmtPctDecimal(sectorLeadership.best.primaryValue, 1)}`
                        : "No sector leader yet"}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Weakest Sector</div>
                    <div style={{ marginTop: "0.45rem", fontSize: "1rem", fontWeight: 700 }}>{sectorLeadership.worst?.name || "—"}</div>
                    <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                      {sectorLeadership.worst?.primaryValue != null
                        ? `${sectorPrimaryLabel} ${fmtPctDecimal(sectorLeadership.worst.primaryValue, 1)}`
                        : "No sector laggard yet"}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Strongest Stock</div>
                    <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>{topStocks[0]?.ticker || "—"}</div>
                    <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                      {getRsValue(topStocks[0], summaryPrimaryKey) != null
                        ? `${summaryPrimaryLabel} ${fmtPctDecimal(getRsValue(topStocks[0], summaryPrimaryKey), 1)}`
                        : "No stock leader yet"}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>Weakest Stock</div>
                    <div style={{ marginTop: "0.45rem", fontSize: "1.2rem", fontWeight: 700 }}>{laggardStocks[0]?.ticker || "—"}</div>
                    <div style={{ marginTop: "0.35rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                      {getRsValue(laggardStocks[0], summaryPrimaryKey) != null
                        ? `${summaryPrimaryLabel} ${fmtPctDecimal(getRsValue(laggardStocks[0], summaryPrimaryKey), 1)}`
                        : "No stock laggard yet"}
                    </div>
                  </div>
                </div>
                <div style={{ display: "grid", gap: "1.2rem", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: "0.75rem" }}>Strong Sectors ({sectorPrimaryLabel})</div>
                    {topSectors.length === 0 ? (
                      <div style={{ color: "var(--color-text-muted)" }}>Sector strength is not available for this index session yet. Check breadth and activity first.</div>
                    ) : (
                      <div style={{ display: "grid", gap: "0.6rem" }}>
                        {topSectors.map((row) => (
                          <div
                            key={row.sector}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.5rem 0.6rem",
                              borderRadius: "10px",
                              background: "rgba(15, 23, 42, 0.5)",
                              border: "1px solid rgba(148, 163, 184, 0.2)",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600 }}>{row.sector}</div>
                              <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                                Weight {fmtPctValue(row.weightPct, 2)} · {RS_WINDOW_LABELS.RS20D} {fmtPctDecimal(row.RS20D, 1)} · {RS_WINDOW_LABELS.RS90D} {fmtPctDecimal(row.RS90D, 1)}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: valueColor(getRsValue(row, sectorPrimaryKey)) }}>
                              {fmtPctDecimal(getRsValue(row, sectorPrimaryKey), 1)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: "0.75rem" }}>Stocks Beating This Index ({summaryPrimaryLabel})</div>
                    {topStocks.length === 0 ? (
                      <div style={{ color: "var(--color-text-muted)" }}>Strong-stock data is not available for this index session yet. Check breadth and liquidity first.</div>
                    ) : (
                      <div style={{ display: "grid", gap: "0.6rem" }}>
                        {topStocks.map((row) => (
                          <div
                            key={row.ticker}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.5rem 0.6rem",
                              borderRadius: "10px",
                              background: "rgba(15, 23, 42, 0.5)",
                              border: "1px solid rgba(148, 163, 184, 0.2)",
                            }}
                          >
                            <div>
                              <Link
                                href={`/stock/${encodeURIComponent(row.ticker)}`}
                                style={{ fontWeight: 600, color: "var(--blue-text)", textDecoration: "none" }}
                              >
                                {row.ticker}
                              </Link>
                              <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                                {row.sector || "Sector N/A"} · {RS_WINDOW_LABELS.RS20D} {fmtPctDecimal(row.RS20D, 1)} · {RS_WINDOW_LABELS.RS90D} {fmtPctDecimal(row.RS90D, 1)}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: valueColor(getRsValue(row, summaryPrimaryKey)) }}>
                              {fmtPctDecimal(getRsValue(row, summaryPrimaryKey), 1)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: "0.75rem" }}>Stocks Lagging This Index ({summaryPrimaryLabel})</div>
                    {laggardStocks.length === 0 ? (
                      <div style={{ color: "var(--color-text-muted)" }}>Laggard data is not available for this index session yet.</div>
                    ) : (
                      <div style={{ display: "grid", gap: "0.6rem" }}>
                        {laggardStocks.map((row) => (
                          <div
                            key={row.ticker}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.5rem 0.6rem",
                              borderRadius: "10px",
                              background: "rgba(15, 23, 42, 0.5)",
                              border: "1px solid rgba(148, 163, 184, 0.2)",
                            }}
                          >
                            <div>
                              <Link
                                href={`/stock/${encodeURIComponent(row.ticker)}`}
                                style={{ fontWeight: 600, color: "var(--blue-text)", textDecoration: "none" }}
                              >
                                {row.ticker}
                              </Link>
                              <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                                {row.sector || "Sector N/A"} · {RS_WINDOW_LABELS.RS20D} {fmtPctDecimal(row.RS20D, 1)} · {RS_WINDOW_LABELS.RS90D} {fmtPctDecimal(row.RS90D, 1)}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: valueColor(getRsValue(row, summaryPrimaryKey)) }}>
                              {fmtPctDecimal(getRsValue(row, summaryPrimaryKey), 1)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div id="breadth" style={{ order: 3 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>Market Breadth</div>
                <div style={{ display: "grid", gap: "1.2rem", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Stocks Participating Today</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.breadth.sessionParticipationPct) }}>
                      {fmtPctValue(detailPayload.breadth.sessionParticipationPct)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Normal Participation</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.breadth.avgParticipation20D) }}>
                      {fmtPctValue(detailPayload.breadth.avgParticipation20D)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Stocks Beating Index</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.breadth.relativeStrength.rsBreadth.positiveRS60D_pct) }}>
                      {detailPayload.breadth.relativeStrength.rsBreadth.positiveRS60D_pct === null
                        ? "—"
                        : fmtPctValue(detailPayload.breadth.relativeStrength.rsBreadth.positiveRS60D_pct)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>52W Expansion</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1rem", color: "var(--color-text-muted)" }}>
                      Highs: {detailPayload.breadth.newHighs ?? "N/A"} · Lows: {detailPayload.breadth.newLows ?? "N/A"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "16px",
                    padding: "1rem",
                    marginTop: "1.2rem",
                    background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>Are More Stocks Rising or Falling?</div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                        Green line = stocks up for the day. Red line = stocks down for the day.
                      </div>
                    </div>
                    {breadthRead && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 800, color: valueColor(breadthRead.netAdvancers) }}>{breadthRead.label}</div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem", marginTop: "0.2rem" }}>
                          {breadthRead.advancers} up / {breadthRead.decliners} down
                        </div>
                      </div>
                    )}
                  </div>
                  {breadthRead && (
                    <div
                      style={{
                        display: "grid",
                        gap: "0.75rem",
                        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                        marginBottom: "0.9rem",
                      }}
                    >
                      <div style={{ padding: "0.7rem", borderRadius: "10px", background: "rgba(15, 23, 42, 0.55)", border: "1px solid rgba(148, 163, 184, 0.18)" }}>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem", fontWeight: 700 }}>Up vs Down Gap</div>
                        <div style={{ marginTop: "0.25rem", fontSize: "1.15rem", fontWeight: 800, color: valueColor(breadthRead.netAdvancers) }}>
                          {breadthRead.netAdvancers > 0 ? "+" : ""}{breadthRead.netAdvancers}
                        </div>
                      </div>
                      <div style={{ padding: "0.7rem", borderRadius: "10px", background: "rgba(15, 23, 42, 0.55)", border: "1px solid rgba(148, 163, 184, 0.18)" }}>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem", fontWeight: 700 }}>Advancer Share</div>
                        <div style={{ marginTop: "0.25rem", fontSize: "1.15rem", fontWeight: 800 }}>
                          {fmtPctValue(breadthRead.advancerShare)}
                        </div>
                      </div>
                      <div style={{ padding: "0.7rem", borderRadius: "10px", background: "rgba(15, 23, 42, 0.55)", border: "1px solid rgba(148, 163, 184, 0.18)" }}>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.78rem", fontWeight: 700 }}>Latest Session</div>
                        <div style={{ marginTop: "0.25rem", fontSize: "1.15rem", fontWeight: 800 }}>
                          {breadthRead.date}
                        </div>
                      </div>
                    </div>
                  )}
                  {detailPayload.breadth.advanceDeclineSeries.length === 0 ? (
                    <div style={{ color: "var(--color-text-muted)" }}>No breadth series.</div>
                  ) : (
                    (() => {
                      const adv = detailPayload.breadth.advanceDeclineSeries.map((r) => r.advancers);
                      const dec = detailPayload.breadth.advanceDeclineSeries.map((r) => r.decliners);
                      const min = Math.min(...adv, ...dec);
                      const max = Math.max(...adv, ...dec);
                      const advPath = buildPath(adv, 600, 180, min, max);
                      const decPath = buildPath(dec, 600, 180, min, max);
                      return (
                        <div>
                          <svg viewBox="0 0 600 180" width="100%" height="180" preserveAspectRatio="none" role="img" aria-label="Advance decline trend showing stocks rising versus stocks falling">
                            <path d={advPath} fill="none" stroke="var(--color-success)" strokeWidth="2.2" />
                            <path d={decPath} fill="none" stroke="var(--color-danger)" strokeWidth="2.2" />
                          </svg>
                          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", color: "var(--color-text-muted)", fontSize: "0.82rem", marginTop: "0.4rem" }}>
                            <span><span style={{ color: "var(--color-success)", fontWeight: 800 }}>Green</span> stocks rising</span>
                            <span><span style={{ color: "var(--color-danger)", fontWeight: 800 }}>Red</span> stocks falling</span>
                          </div>
                        </div>
                      );
                    })()
                  )}
                </div>

                {breadthRead && (
                  <div style={{ marginTop: "0.8rem", color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
                    {breadthRead.detail}
                  </div>
                )}
                {breadthSummary && (
                  <div style={{ marginTop: "0.8rem", color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
                    {breadthSummary}
                  </div>
                )}
              </div>

              <div id="heavyweights" style={{ order: 5 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>
                  Is the Move Broad or Heavyweight-Driven?
                </div>
                {concentrationRead && (
                  <div
                    style={{
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      borderRadius: "12px",
                      padding: "0.85rem 1rem",
                      marginBottom: "1rem",
                      background: "rgba(15, 23, 42, 0.55)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 800 }}>{concentrationRead.label}</div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                          {concentrationRead.detail}
                        </div>
                      </div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", textAlign: "right" }}>
                        Top 10 control {fmtPctValue(concentrationRead.top10Weight, 1)}
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gap: "1.2rem", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Top 5 Stocks Control</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.concentration.top5Weight) }}>
                      {fmtPctValue(detailPayload.concentration.top5Weight)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Top 10 Stocks Control</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.concentration.top10Weight) }}>
                      {fmtPctValue(detailPayload.concentration.top10Weight)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Crowding vs 3M</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.concentration.vs3MonthAverage) }}>
                      {fmtPctValue(detailPayload.concentration.vs3MonthAverage)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Top 3 Drive</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.concentration.top3ImpactPct) }}>
                      {fmtPctValue(detailPayload.concentration.top3ImpactPct)}
                    </div>
                  </div>
                </div>

                {detailPayload.interpretation?.concentrationText && (
                  <div style={{ marginTop: "0.8rem", color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
                    {detailPayload.interpretation.concentrationText}
                  </div>
                )}
              </div>

              <div id="liquidity" style={{ order: 6 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>Is Money Active Today?</div>
                {liquidityRead && (
                  <div
                    style={{
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      borderRadius: "12px",
                      padding: "0.85rem 1rem",
                      marginBottom: "1rem",
                      background: "rgba(15, 23, 42, 0.55)",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{liquidityRead.label}</div>
                    <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                      {liquidityRead.detail}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gap: "1.2rem", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Activity vs Normal</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.3rem", fontWeight: 700, color: valueColor(detailPayload.liquidity.vs30DayAvgPct) }}>
                      {fmtPctValue(detailPayload.liquidity.vs30DayAvgPct)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Normal Value</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.1rem", fontWeight: 700 }}>
                      {fmtPkrCompact(detailPayload.liquidity.avgTradedValue20D)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Volume Direction</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.1rem", fontWeight: 700 }}>
                      {detailPayload.liquidity.volumeTrend}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Activity Read</div>
                    <div style={{ marginTop: "0.5rem", fontSize: "1.1rem", fontWeight: 700 }}>
                      {detailPayload.liquidity.regimeLabel || "N/A"}
                    </div>
                  </div>
                </div>

                {detailPayload.interpretation?.liquidityText && (
                  <div style={{ marginTop: "0.8rem", color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
                    {detailPayload.interpretation.liquidityText}
                  </div>
                )}
              </div>

              <div id="index-faq" style={{ order: 9 }}>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.8rem" }}>
                  Quick Help
                </div>
                <div style={{ display: "grid", gap: "0.75rem" }}>
                  {faqItems.map((item) => (
                    <details
                      key={item.question}
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "10px",
                        padding: "0.85rem 1rem",
                        background: "rgba(15, 23, 42, 0.48)",
                      }}
                    >
                      <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                        {item.question}
                      </summary>
                      <div style={{ marginTop: "0.55rem", color: "var(--color-text-muted)", fontSize: "0.92rem", lineHeight: 1.55 }}>
                        {item.answer}
                      </div>
                    </details>
                  ))}
                </div>
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
